import * as Dialog from '@radix-ui/react-dialog';
import { Check, Copy, MailWarning } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';

import { Button } from '@/components/ui/button';
import { Field, FormError } from '@/components/ui/field';
import { FormSection, RequiredFieldsNote } from '@/components/ui/form-section';
import { useConvertLead, type ConvertLeadResponse } from '@/hooks/leads';
import { ApiError } from '@/lib/api';
import { useToast } from '@/lib/toast-context';
import { suggestNameSplit } from '@shared/schemas/applicant';
import { DEFAULT_STATE } from '@shared/schemas/intake';
import type { LeadDetail } from '@shared/schemas/lead';

/*
 * Convert a lead into a customer — ROADMAP.md §9.9.
 *
 * **Two things this dialog exists to put in front of a person**, both because
 * getting them wrong is not undoable:
 *
 *   1. **The name split.** The quote form has one Name field, so `lead` holds
 *      the whole string in `first_name` — and `client` needs a first and a last
 *      name. `suggestNameSplit` prefills it and the original is shown beside it.
 *   2. **The street address.** A quote request carries a city, a state and a zip
 *      but never a street address, so the first property cannot be created from
 *      it. The bedrooms, bathrooms and square footage they described are shown
 *      here, which is what tells the office what they are typing an address for.
 */

export function ConvertLeadDialog({
  detail,
  open,
  onOpenChange,
  onConverted,
}: {
  detail: LeadDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConverted: () => void;
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 max-h-[calc(100vh-2rem)] w-[calc(100vw-2rem)] max-w-2xl -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-lg border border-ink-300 bg-white p-6 shadow-pop">
          <ConvertForm
            detail={detail}
            onDone={() => {
              onOpenChange(false);
            }}
            onConverted={onConverted}
          />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function ConvertForm({
  detail,
  onDone,
  onConverted,
}: {
  detail: LeadDetail;
  onDone: () => void;
  onConverted: () => void;
}) {
  const toast = useToast();
  const suggestion = suggestNameSplit(detail.displayName);

  const [firstName, setFirstName] = useState(suggestion.firstName);
  const [lastName, setLastName] = useState(suggestion.lastName);
  const [businessName, setBusinessName] = useState(detail.businessName ?? '');

  const [withProperty, setWithProperty] = useState(detail.properties.length > 0);
  const [address, setAddress] = useState('');
  const [city, setCity] = useState(detail.city ?? '');
  const [state, setState] = useState(detail.state ?? DEFAULT_STATE);
  const [zip, setZip] = useState(detail.postalCode ?? '');

  const [handover, setHandover] = useState<ConvertLeadResponse | null>(null);

  const convert = useConvertLead(detail.id);
  const error = convert.error instanceof ApiError ? convert.error : null;
  const fieldErrors = error?.fieldErrors ?? {};

  if (handover) {
    return (
      <Handover
        result={handover}
        onDone={() => {
          onDone();
          onConverted();
        }}
      />
    );
  }

  function submit(event: FormEvent) {
    event.preventDefault();

    convert.mutate(
      {
        firstName,
        lastName,
        businessName: businessName.trim() === '' ? null : businessName.trim(),
        ...(withProperty ? { property: { address, city, state, zip } } : {}),
      },
      {
        onSuccess: (result) => {
          if (!result.created) {
            // The idempotent answer — a double-clicked Convert gets the same
            // customer twice, not two customers.
            toast.success(`${result.clientName} was already created from this lead.`);
            onDone();
            return;
          }
          setHandover(result);
        },
      },
    );
  }

  const firstProperty = detail.properties[0];

  return (
    <>
      <Dialog.Title className="text-h2 text-ink-900">
        Convert {detail.businessName ?? detail.displayName}
      </Dialog.Title>
      <Dialog.Description className="mt-1 text-body text-ink-500">
        This creates their client record and their portal login, and marks the lead converted.
      </Dialog.Description>

      <form onSubmit={submit} className="mt-5 space-y-6">
        <RequiredFieldsNote />

        {error && Object.keys(fieldErrors).length === 0 ? (
          <FormError>{error.message}</FormError>
        ) : null}

        <FormSection
          title="Their name"
          description="The quote form asks for one name. Check the split before it becomes the name on their record."
        >
          <p className="rounded-sm border border-ink-300 bg-ink-100/40 px-3 py-2 text-body text-ink-700">
            <span className="text-meta text-ink-500">They wrote: </span>
            {detail.displayName}
          </p>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="First name"
              name="firstName"
              required
              value={firstName}
              error={fieldErrors.firstName}
              onChange={(event) => {
                setFirstName(event.target.value);
              }}
            />
            <Field
              label="Last name"
              name="lastName"
              required
              value={lastName}
              error={fieldErrors.lastName}
              onChange={(event) => {
                setLastName(event.target.value);
              }}
            />
          </div>
          <Field
            label="Business name"
            name="businessName"
            value={businessName}
            error={fieldErrors.businessName}
            onChange={(event) => {
              setBusinessName(event.target.value);
            }}
            hint="Only if they are a business — a property manager, an office."
          />
        </FormSection>

        <FormSection
          title="Their first property"
          description="Optional. Properties can be added on the client record afterwards."
        >
          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              name="withProperty"
              checked={withProperty}
              onChange={(event) => {
                setWithProperty(event.target.checked);
              }}
              className="mt-1 size-4"
            />
            <span className="text-body text-ink-900">Add a property now</span>
          </label>

          {withProperty ? (
            <>
              {firstProperty ? (
                /*
                 * **What they described, so the office knows what they are
                 * typing an address for.** The band is shown as a band — the
                 * customer chose a range and a midpoint would be a number they
                 * never said.
                 */
                <p className="rounded-sm border border-ink-300 bg-honey-50 px-3 py-2 text-body text-bark-900">
                  <span className="text-meta">Property 1 on their quote: </span>
                  {firstProperty.bedrooms} bed · {firstProperty.bathroomsLabel} bath ·{' '}
                  {firstProperty.squareFeetBandLabel} sq ft
                </p>
              ) : null}

              <Field
                label="Street address"
                name="address"
                required
                value={address}
                error={fieldErrors['property.address']}
                onChange={(event) => {
                  setAddress(event.target.value);
                }}
                hint="The quote form never asks for one, so this is the one thing you have to get from them."
              />
              <div className="grid gap-4 sm:grid-cols-3">
                <Field
                  label="City"
                  name="city"
                  required
                  value={city}
                  error={fieldErrors['property.city']}
                  onChange={(event) => {
                    setCity(event.target.value);
                  }}
                />
                <Field
                  label="State"
                  name="state"
                  required
                  value={state}
                  error={fieldErrors['property.state']}
                  onChange={(event) => {
                    setState(event.target.value);
                  }}
                />
                <Field
                  label="ZIP"
                  name="zip"
                  required
                  value={zip}
                  error={fieldErrors['property.zip']}
                  onChange={(event) => {
                    setZip(event.target.value);
                  }}
                />
              </div>

              {firstProperty ? (
                <p className="text-meta text-ink-500">
                  The bedrooms and bathrooms come across. The square footage stays on the lead —
                  they gave a range and a property record wants a number, so writing one would be
                  inventing it.
                </p>
              ) : null}
            </>
          ) : null}
        </FormSection>

        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Dialog.Close asChild>
            <Button variant="outline" disabled={convert.isPending}>
              Cancel
            </Button>
          </Dialog.Close>
          <Button type="submit" disabled={convert.isPending}>
            {convert.isPending ? 'Converting…' : 'Convert to client'}
          </Button>
        </div>
      </form>
    </>
  );
}

/**
 * What happened, including what did not.
 *
 * **The welcome email usually goes now** — outbound mail started working on
 * 2026-08-05 — but this still reports which happened rather than assuming.
 * `sendEmail` never throws, so a send that fails leaves a `failed` row in
 * `email_log` and the conversion stands; showing "we have emailed them"
 * unconditionally would send somebody off expecting a reply to a message that
 * never left the building.
 */
function Handover({ result, onDone }: { result: ConvertLeadResponse; onDone: () => void }) {
  const [copied, setCopied] = useState(false);
  const password = result.temporaryPassword;

  return (
    <>
      <Dialog.Title className="text-h2 text-ink-900">{result.clientName} is a client</Dialog.Title>
      <Dialog.Description className="mt-1 text-body text-ink-500">
        Their client record is created and the lead is linked to it.
      </Dialog.Description>

      {password === null ? (
        <div className="mt-5 rounded-sm border border-ink-300 bg-ink-100/40 p-4">
          <p className="text-body text-ink-900">No portal login was created.</p>
          <p className="mt-1 text-meta text-ink-500">
            {result.portalLoginNote ?? 'Create one from their client record when they need it.'}
          </p>
        </div>
      ) : (
        <dl className="mt-5 space-y-3 rounded-sm border border-ink-300 bg-ink-100/40 p-4">
          <div>
            <dt className="text-meta text-ink-500">They sign in with</dt>
            <dd className="break-all text-body text-ink-900">their email address</dd>
          </div>
          <div>
            <dt className="text-meta text-ink-500">Temporary password</dt>
            <dd className="mt-1 flex flex-wrap items-center gap-2">
              <code className="min-w-0 break-all rounded-sm border border-ink-300 bg-white px-2 py-1.5 text-body">
                {password}
              </code>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  void navigator.clipboard.writeText(password).then(() => {
                    setCopied(true);
                  });
                }}
              >
                {copied ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
                {copied ? 'Copied' : 'Copy'}
              </Button>
            </dd>
          </div>
        </dl>
      )}

      {result.welcomeEmailSent ? (
        <p className="mt-3 text-meta text-ink-500">A welcome email is on its way to them.</p>
      ) : (
        <p className="mt-3 flex items-start gap-2 rounded-sm border border-ink-300 bg-honey-50 px-3 py-2.5 text-meta text-bark-900">
          <MailWarning aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
          <span>
            The welcome email did not go out. The attempt and the reason are recorded on the Email
            page. Tell them yourself, and give them the password over something private.
          </span>
        </p>
      )}

      <div className="mt-6 flex flex-wrap justify-end gap-2">
        <Button asChild variant="outline">
          <Link to={`/admin/clients/${result.clientId}`}>Open their client record</Link>
        </Button>
        <Button onClick={onDone}>Done</Button>
      </div>
    </>
  );
}
